import { supabase } from "@/integrations/supabase/client";

export interface ConversationQuality {
  quality_score: number;
  frustration_signals: string[];
  auto_booking_triggered: boolean;
}

/**
 * Get conversation quality metrics for a specific conversation
 */
export async function getConversationQuality(
  conversationId: string
): Promise<ConversationQuality | null> {
  const { data, error } = await supabase
    .from("chat_conversations")
    .select("quality_score, frustration_signals, auto_booking_triggered")
    .eq("id", conversationId)
    .single();

  if (error) {
    console.error("Error fetching conversation quality:", error);
    return null;
  }

  return {
    quality_score: data.quality_score ?? 100,
    frustration_signals: (data.frustration_signals as string[]) ?? [],
    auto_booking_triggered: data.auto_booking_triggered ?? false,
  };
}

/**
 * Get quality trends across all conversations for analytics
 */
export async function getQualityAnalytics() {
  const { data, error } = await supabase
    .from("chat_conversations")
    .select("quality_score, auto_booking_triggered, started_at, booking_scheduled")
    .order("started_at", { ascending: false })
    .limit(100);

  if (error) {
    console.error("Error fetching quality analytics:", error);
    return null;
  }

  const avgQualityScore =
    data.reduce((sum, conv) => sum + (conv.quality_score ?? 100), 0) / data.length;
  const autoBookingRate =
    data.filter((conv) => conv.auto_booking_triggered).length / data.length;
  const bookingConversionRate =
    data.filter((conv) => conv.auto_booking_triggered && conv.booking_scheduled).length /
    data.filter((conv) => conv.auto_booking_triggered).length;

  return {
    avgQualityScore: Math.round(avgQualityScore),
    autoBookingRate: Math.round(autoBookingRate * 100),
    bookingConversionRate: Math.round((bookingConversionRate || 0) * 100),
    totalConversations: data.length,
    lowQualityCount: data.filter((conv) => (conv.quality_score ?? 100) < 60).length,
  };
}
